/* =========================================================
   GLOBAL CAROUSEL (GC)
   File: assets/global-carousel.js
   ========================================================= */

(function () {
  function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }

  function isDesktopPointer() {
    return window.matchMedia('(hover:hover) and (pointer:fine)').matches;
  }

  // ✅ don’t hijack clicks on links / buttons inside the carousel
  function isInteractiveTarget(el) {
    if (!el || !el.closest) return false;
    return !!el.closest('a, button, input, textarea, select, label, summary, [role="button"]');
  }

  function getSlides(track, root) {
    const byAttr = Array.from(root.querySelectorAll('[data-carousel-slide]'));
    if (byAttr.length) return byAttr;
    return Array.from(track.children || []).filter(Boolean);
  }

  function getCenteredIndex(track, slides) {
    const rect = track.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;

    let best = 0;
    let bestDist = Infinity;

    slides.forEach((s, i) => {
      const r = s.getBoundingClientRect();
      const sx = r.left + r.width / 2;
      const d = Math.abs(centerX - sx);
      if (d < bestDist) { bestDist = d; best = i; }
    });

    return best;
  }

  function scrollToIndex(track, slides, idx) {
    idx = clamp(idx, 0, slides.length - 1);
    const target = slides[idx];
    if (!target) return;

    const left = target.offsetLeft - (track.clientWidth - target.clientWidth) / 2;
    track.scrollTo({ left, behavior: 'smooth' });
  }

  function updateProgress(root, track) {
    const bar = root.querySelector('[data-carousel-progress]');
    if (!bar) return;

    const max = Math.max(1, track.scrollWidth - track.clientWidth);
    const pct = clamp(track.scrollLeft / max, 0, 1);
    bar.style.transform = `scaleX(${pct})`;
  }

  function initOne(root) {
    if (!root || root.__gcInit) return;
    root.__gcInit = true;

    const track = root.querySelector('[data-carousel-track]');
    if (!track) return;

    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    const dotsWrap = root.querySelector('[data-carousel-dots]');
    const slides = getSlides(track, root);

    // baseline progress
    updateProgress(root, track);

    if (slides.length <= 1) return;

    // Build dots
    let dots = [];
    if (dotsWrap) {
      dotsWrap.innerHTML = '';
      dots = slides.map((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'gc-dot';
        b.setAttribute('aria-label', `Go to slide ${i + 1}`);
        b.addEventListener('click', () => scrollToIndex(track, slides, i));
        dotsWrap.appendChild(b);
        return b;
      });
    }

    function refreshUI() {
      const idx = getCenteredIndex(track, slides);

      if (dots.length) {
        dots.forEach((d, i) => {
          const isActive = i === idx;
          d.classList.toggle('is-active', isActive);
          d.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }

      updateProgress(root, track);
    }

    // Arrows
    if (prev) prev.addEventListener('click', () => {
      const idx = getCenteredIndex(track, slides);
      scrollToIndex(track, slides, idx - 1);
    });

    if (next) next.addEventListener('click', () => {
      const idx = getCenteredIndex(track, slides);
      scrollToIndex(track, slides, idx + 1);
    });

    // Scroll sync
    let raf = null;
    track.addEventListener('scroll', () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(refreshUI);
    }, { passive: true });

    // Drag-to-scroll (desktop pointer)
    let isDown = false;
    let startX = 0;
    let startLeft = 0;
    let dragged = false;
    let activePointerId = null;

    track.addEventListener('pointerdown', (e) => {
      if (!isDesktopPointer()) return;
      if (e.button !== 0) return;

      // ✅ if clicking CTA/link/button inside slide, do NOT start drag
      if (isInteractiveTarget(e.target)) return;

      isDown = true;
      dragged = false;
      activePointerId = e.pointerId;
      startX = e.clientX;
      startLeft = track.scrollLeft;

      track.classList.add('is-dragging');
      track.setPointerCapture(activePointerId);

      // keep this for drag feel — but now it won't run on links
      e.preventDefault();
    });

    track.addEventListener('pointermove', (e) => {
      if (!isDown) return;
      if (activePointerId !== e.pointerId) return;

      const dx = e.clientX - startX;

      // ✅ threshold: only treat as drag after a bit of movement
      if (Math.abs(dx) > 6) dragged = true;

      track.scrollLeft = startLeft - dx;
      e.preventDefault();
    });

    const endDrag = (e) => {
      if (!isDown) return;
      if (activePointerId !== e.pointerId && e.type !== 'pointerleave') return;

      isDown = false;
      track.classList.remove('is-dragging');

      try { track.releasePointerCapture(activePointerId); } catch (err) {}
      activePointerId = null;

      // ✅ only cancel click when an actual drag occurred
      if (dragged) {
        const cancelClick = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
        track.addEventListener('click', cancelClick, { capture: true, once: true });
      }
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);

    refreshUI();
  }

  function initAll(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-carousel]').forEach(initOne);
  }

  document.addEventListener('DOMContentLoaded', () => initAll(document));
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:block:select', (e) => initAll(e.target));
})();









/* =========================================================
   GLOBAL MEDIA OVERLAY / LIGHTBOX
   Opens on true tap only
   Allows carousel drag/swipe on image area
   Preserves page + carousel position cleanly
   ========================================================= */

(() => {
  if (window.__wjMediaOverlayBound) return;
  window.__wjMediaOverlayBound = true;

  const SELECTOR = '[data-media-overlay-trigger]';
  const DRAG_THRESHOLD = 12;
  const CLOSE_MS = 280;

  let overlay = null;
  let mediaWrap = null;
  let captionEl = null;
  let captionWrap = null;
  let closeBtn = null;
  let backdropBtn = null;
  let activeTrigger = null;
  let lastScrollY = 0;

  let activeCarouselTrack = null;
  let activeCarouselLeft = 0;
  let activeCarouselSnap = '';
  let activeCarouselBehavior = '';

  let gesture = {
    trigger: null,
    startX: 0,
    startY: 0,
    moved: false,
    pointerId: null
  };

  let suppressClickUntil = 0;

  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, (m) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }[m]));
  }

  function ensureOverlay() {
    if (overlay) return overlay;

    overlay = document.createElement('div');
    overlay.className = 'wj-mediaOverlay';
    overlay.hidden = true;
    overlay.setAttribute('aria-hidden', 'true');

    overlay.innerHTML = `
      <button class="wj-mediaOverlay__backdrop" type="button" aria-label="Close media overlay"></button>

      <button class="wj-mediaOverlay__close" type="button" aria-label="Close media overlay">
        <span class="wj-mediaOverlay__closeIcon" aria-hidden="true">×</span>
      </button>

      <div class="wj-mediaOverlay__stage">
        <div class="wj-mediaOverlay__mediaWrap"></div>
      </div>

      <div class="wj-mediaOverlay__captionWrap">
        <div class="wj-mediaOverlay__captionInner">
          <p class="wj-mediaOverlay__caption"></p>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    mediaWrap = overlay.querySelector('.wj-mediaOverlay__mediaWrap');
    captionEl = overlay.querySelector('.wj-mediaOverlay__caption');
    captionWrap = overlay.querySelector('.wj-mediaOverlay__captionWrap');
    closeBtn = overlay.querySelector('.wj-mediaOverlay__close');
    backdropBtn = overlay.querySelector('.wj-mediaOverlay__backdrop');

    closeBtn.addEventListener('click', closeOverlay);
    backdropBtn.addEventListener('click', closeOverlay);

    document.addEventListener('keydown', (e) => {
      if (!overlay.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeOverlay();
    });

    return overlay;
  }

  function buildMedia(trigger) {
    const type = (trigger.dataset.mediaOverlayType || 'image').toLowerCase();
    const src = trigger.dataset.mediaOverlaySrc || '';
    const poster = trigger.dataset.mediaOverlayPoster || '';
    const alt = trigger.dataset.mediaOverlayAlt || '';
    const mime = trigger.dataset.mediaOverlayMime || '';
    const caption = trigger.dataset.mediaOverlayCaption || '';

    mediaWrap.innerHTML = '';
    captionEl.innerHTML = '';

    if (!src) return false;

    let node;

    if (type === 'video') {
      node = document.createElement('video');
      node.className = 'wj-mediaOverlay__media is-video';
      node.setAttribute('controls', '');
      node.setAttribute('playsinline', '');
      node.setAttribute('preload', 'metadata');

      if (poster) node.poster = poster;

      const source = document.createElement('source');
      source.src = src;
      if (mime) source.type = mime;
      node.appendChild(source);
    } else {
      node = document.createElement('img');
      node.className = 'wj-mediaOverlay__media is-image';
      node.src = src;
      node.alt = alt || '';
      node.loading = 'eager';
      node.decoding = 'async';
      node.draggable = false;
    }

    mediaWrap.appendChild(node);

    if (caption && caption.trim() !== '') {
      captionEl.innerHTML = escapeHtml(caption);
      captionWrap.hidden = false;
    } else {
      captionWrap.hidden = true;
    }

    return true;
  }

  function lockScroll() {
    lastScrollY = window.scrollY || window.pageYOffset || 0;

    document.documentElement.classList.add('wj-overlay-open');
    document.body.classList.add('wj-overlay-open');

    document.body.style.position = 'fixed';
    document.body.style.top = `-${lastScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    document.documentElement.classList.remove('wj-overlay-open');
    document.body.classList.remove('wj-overlay-open');

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';

    window.scrollTo(0, lastScrollY);
  }

  function freezeActiveCarousel(trigger) {
    activeCarouselTrack =
      trigger.closest('[data-carousel]')?.querySelector('[data-carousel-track]') || null;

    if (!activeCarouselTrack) return;

    activeCarouselLeft = activeCarouselTrack.scrollLeft;
    activeCarouselSnap = activeCarouselTrack.style.scrollSnapType || '';
    activeCarouselBehavior = activeCarouselTrack.style.scrollBehavior || '';

    activeCarouselTrack.style.scrollSnapType = 'none';
    activeCarouselTrack.style.scrollBehavior = 'auto';
    activeCarouselTrack.scrollLeft = activeCarouselLeft;
  }

  function restoreActiveCarousel() {
    if (!activeCarouselTrack) return;

    activeCarouselTrack.style.scrollSnapType = 'none';
    activeCarouselTrack.style.scrollBehavior = 'auto';
    activeCarouselTrack.scrollLeft = activeCarouselLeft;

    requestAnimationFrame(() => {
      if (!activeCarouselTrack) return;
      activeCarouselTrack.scrollLeft = activeCarouselLeft;
      activeCarouselTrack.style.scrollSnapType = activeCarouselSnap || '';
      activeCarouselTrack.style.scrollBehavior = activeCarouselBehavior || '';
    });
  }

  function resetActiveCarouselState() {
    activeCarouselTrack = null;
    activeCarouselLeft = 0;
    activeCarouselSnap = '';
    activeCarouselBehavior = '';
  }

  function openOverlay(trigger) {
    ensureOverlay();
    if (!buildMedia(trigger)) return;

    activeTrigger = trigger;
    freezeActiveCarousel(trigger);

    overlay.hidden = false;
    overlay.setAttribute('aria-hidden', 'false');

    lockScroll();

    requestAnimationFrame(() => {
      if (activeCarouselTrack) {
        activeCarouselTrack.scrollLeft = activeCarouselLeft;
      }

      overlay.classList.add('is-open');
      closeBtn.focus({ preventScroll: true });
    });
  }

  function closeOverlay() {
    if (!overlay || !overlay.classList.contains('is-open')) return;

    const video = overlay.querySelector('video');
    if (video) {
      try { video.pause(); } catch (err) {}
    }

    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');

    setTimeout(() => {
      overlay.hidden = true;
      mediaWrap.innerHTML = '';
      captionEl.innerHTML = '';

      unlockScroll();
      restoreActiveCarousel();

      if (activeTrigger && typeof activeTrigger.blur === 'function') {
        activeTrigger.blur();
      }

      activeTrigger = null;
      resetActiveCarouselState();
    }, CLOSE_MS);
  }

  function getTrigger(target) {
    return target ? target.closest(SELECTOR) : null;
  }

  document.addEventListener('pointerdown', (e) => {
    const trigger = getTrigger(e.target);
    if (!trigger) return;

    gesture.trigger = trigger;
    gesture.startX = e.clientX;
    gesture.startY = e.clientY;
    gesture.moved = false;
    gesture.pointerId = e.pointerId;
  }, true);

  document.addEventListener('pointermove', (e) => {
    if (!gesture.trigger) return;
    if (gesture.pointerId !== null && e.pointerId !== gesture.pointerId) return;

    const dx = e.clientX - gesture.startX;
    const dy = e.clientY - gesture.startY;

    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
      gesture.moved = true;
    }
  }, true);

  function endGesture() {
    if (gesture.moved) {
      suppressClickUntil = Date.now() + 350;
    }

    gesture.trigger = null;
    gesture.pointerId = null;
    gesture.moved = false;
    gesture.startX = 0;
    gesture.startY = 0;
  }

  document.addEventListener('pointerup', endGesture, true);
  document.addEventListener('pointercancel', endGesture, true);

  document.addEventListener('click', (e) => {
    const trigger = getTrigger(e.target);
    if (!trigger) return;

    if (Date.now() < suppressClickUntil) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    openOverlay(trigger);
  }, true);
})();



// =========================================================
// WJ GLOBAL VIDEO OVERLAY SYSTEM
// =========================================================
(function () {
  function initWJVideos(scope) {
    const root = scope || document;
    const wraps = root.querySelectorAll('.js-wj-videoWrap');

    wraps.forEach((wrap) => {
      if (wrap.dataset.wjVideoInit === 'true') return;
      wrap.dataset.wjVideoInit = 'true';

      const video = wrap.querySelector('.js-wj-video');
      const playBtn = wrap.querySelector('.js-wj-videoPlay');

      if (!video || !playBtn) return;

      function showNativeControls() {
        video.setAttribute('controls', 'controls');
        video.controls = true;
      }

      function hideOverlay() {
        playBtn.classList.add('is-hidden');
      }

      function showOverlay() {
        playBtn.classList.remove('is-hidden');
      }

      playBtn.addEventListener('click', () => {
        showNativeControls();
        hideOverlay();

        const promise = video.play();
        if (promise && typeof promise.catch === 'function') {
          promise.catch(() => {});
        }
      });

      video.addEventListener('play', () => {
        showNativeControls();
        hideOverlay();
      });

      video.addEventListener('ended', () => {
        showOverlay();
      });

      video.addEventListener('pause', () => {
        if (video.currentTime === 0 || video.ended) {
          showOverlay();
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWJVideos(document));
  } else {
    initWJVideos(document);
  }

  window.WJInitVideos = initWJVideos;
})();




// =========================================================
// SPLIT CAROUSEL — caption sync only
// Works with global carousel scroll behavior
// =========================================================
(function () {
  function initSplitCarouselCaptionSync(root) {
    const track = root.querySelector('[data-wj-split-carousel-track]');
    const captionTarget = root.parentElement?.querySelector('[data-wj-split-carousel-caption]');
    const slides = Array.from(root.querySelectorAll('[data-wj-split-carousel-slide]'));

    if (!track || !captionTarget || !slides.length) return;
    if (root.dataset.wjSplitCaptionInit === 'true') return;
    root.dataset.wjSplitCaptionInit = 'true';

    function getCenteredIndex() {
      const rect = track.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;

      let best = 0;
      let bestDist = Infinity;

      slides.forEach((slide, i) => {
        const r = slide.getBoundingClientRect();
        const sx = r.left + r.width / 2;
        const d = Math.abs(centerX - sx);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });

      return best;
    }

    function updateCaption() {
      const idx = getCenteredIndex();
      const active = slides[idx];
      if (!active) return;

      const cap = active.querySelector('.wj-splitCarousel__slideCaption');
      const html = cap ? cap.innerHTML.trim() : '';

      if (html) {
        captionTarget.innerHTML = html;
        captionTarget.hidden = false;
      } else {
        captionTarget.innerHTML = '';
        captionTarget.hidden = true;
      }
    }

    let raf = null;
    track.addEventListener('scroll', () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateCaption);
    }, { passive: true });

    window.addEventListener('load', updateCaption, { passive: true });
    window.addEventListener('resize', updateCaption, { passive: true });

    updateCaption();
  }

  function initAllSplitCaptionSync(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-wj-split-carousel]').forEach(initSplitCarouselCaptionSync);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllSplitCaptionSync(document));
  } else {
    initAllSplitCaptionSync(document);
  }

  document.addEventListener('shopify:section:load', (e) => initAllSplitCaptionSync(e.target));
  document.addEventListener('shopify:block:select', (e) => initAllSplitCaptionSync(e.target));
})();





